import React from 'react'
import styled, { css } from 'styled-components'

import { color as colorSelector } from '../../styles/color'
import { TRANSITION_TIME_MD_MS } from '../../styles/mixins/constants'
import { AppTheme } from '../../styles/theme'
import { useTheme } from '../../styles/ThemeContext'
import { printWarning } from '../../util/print'
import { insertTestingSelector, TestingKeyParams } from '../../util/testing'

import * as AntIcons from './antDirectory'
import * as CustomIcons from './SVG'

export type SvgIcon = React.FunctionComponent<{
  style?: Record<string, unknown>
}>
type InternalSVG = keyof typeof CustomIcons

export type IconKey = keyof typeof AntIcons | InternalSVG

export type IconColor = keyof AppTheme['colors']['icon']

const isInternalIconKey = (key: unknown): key is InternalSVG => {
  return !!Object.keys(CustomIcons).find((iconKey) => iconKey === key)
}

export const ICON_SIZING_PX = [0, 12, 14, 16, 18, 24, 32, 40, 48, 56, 64, 72] as const
export type IconSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
export const Icon = ({
  iconKey,
  color = 'primary',
  activeColor = 'activity',
  size = 1,
  className,
  style = {},
  testingSelectors,
  customSize,
  disabled,
  spin,
  onClick,
}: {
  iconKey: IconKey
  color?: IconColor | string
  activeColor?: IconColor | string
  size?: IconSize
  className?: string
  style?: React.CSSProperties
  testingSelectors?: TestingKeyParams
  customSize?: boolean
  disabled?: boolean
  spin?: boolean
  onClick?: (e: React.MouseEvent) => void
}): JSX.Element | null => {
  const theme = useTheme()

  let iconHexColor

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    iconHexColor = colorSelector.icon[color]({ theme })
  } catch {
    iconHexColor = color
  }

  let iconActiveHexColor

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    iconActiveHexColor = colorSelector.icon[activeColor]({ theme })
  } catch {
    iconActiveHexColor = activeColor
  }

  let Component
  let isInternal = false
  // eslint-disable-next-line no-cond-assign
  if ((isInternal = isInternalIconKey(iconKey))) Component = CustomIcons[iconKey]
  else Component = AntIcons[iconKey]

  if (!Component) {
    // eslint-disable-next-line no-console
    printWarning(`Invalid icon key ${iconKey}`)
    return null
  }

  let testingParams = {}
  if (testingSelectors) {
    testingParams = insertTestingSelector(...testingSelectors)
  }
  return (
    <Container
      size={`${ICON_SIZING_PX[size]}px`}
      className={className}
      onClick={disabled ? undefined : onClick}
      isControl={!disabled && !!onClick}
      isActiveControl={!disabled && !!onClick && color === 'activity'}
      isInternal={!!isInternal}
      colorHex={iconHexColor}
      activeColorHex={iconActiveHexColor}
      customSize={Boolean(customSize)}
      disabled={Boolean(disabled)}
      {...testingParams}
    >
      <Component
        disabled={Boolean(disabled)}
        spin={spin}
        style={{
          ...style,
          color: iconHexColor,
          fontSize: `${ICON_SIZING_PX[size]}px`,
        }}
      />
    </Container>
  )
}

const Container = styled.div<{
  size: string
  isInternal: boolean
  colorHex: string
  activeColorHex: string
  isControl: boolean
  customSize: boolean
  disabled: boolean
  isActiveControl: boolean
}>`
  ${(p) =>
    !p.customSize &&
    `
  width: ${p.size};
  height: ${p.size};
  max-width: ${p.size};
  max-height: ${p.size};
  min-height: 0;
  min-width: 0;
  `}
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.isInternal &&
    css`
      svg * {
        fill: ${p.colorHex};
      }
    `}
  ${(p) =>
    p.isControl &&
    css`
      cursor: pointer;
      svg {
        transition: all ${TRANSITION_TIME_MD_MS}ms;
        &:hover {
          color: ${p.activeColorHex};
        }
        &:active {
          opacity: 0.5;
        }
      }

      ${p.isActiveControl &&
      css`
        color: ${colorSelector.brand.primary};
        &:hover {
          opacity: 0.5;
          color: ${colorSelector.brand.primary};
        }
        &:active {
          opacity: 1;
          color: ${colorSelector.brand.primary};
        }
      `}

      ${p.isInternal &&
      css`
        &:hover {
          svg * {
            fill: ${colorSelector.brand.primary};
          }
        }
      `}
    `}
    ${(p) =>
    p.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`
